import { directionsUrl, type SharePayload } from '@/utils/share';
import { memberBoundaryCentroid } from '@/utils/memberParcelCentroid';

/**
 * The shape this builder reads off a hydrated FPA member. Loose on purpose —
 * both callers are untyped .jsx pages handing over the raw API response.
 */
export interface ShareableMember {
  name?: string | null;
  plot_number?: string | null;
  street?: string | null;
  area_suburb?: string | null;
  hazard_tags?: string[] | null;
  animal_tags?: string[] | null;
  contacts?: Array<{ name?: string | null; phone?: string | null }> | null;
  // GeoJSON geometry of the linked parcel, when there is one.
  parcel_boundary?: { type?: string; coordinates?: unknown } | null;
}

/** Tags are stored snake_case; the UI shows them spaced, so share them that way. */
export function formatTags(tags?: string[] | null): string {
  return (tags || []).map((t) => String(t).replace(/_/g, ' ')).join(', ');
}

/**
 * Build the share payload for an FPA member.
 *
 * Shared by the members list (which fetches the member first, because the list
 * payload deliberately blanks contacts) and the member detail page (which
 * already holds the hydrated record). Keeping one builder means the two can't
 * drift into sharing different fields.
 *
 * Deliberately excluded: ref_number and member_type. Both are internal —
 * the reference is an admin/system identifier and membership status is a
 * billing matter, neither of which means anything to whoever receives this
 * on their phone on the way to a property.
 *
 * Contacts carry name and number only — no email, per the brief.
 *
 * `centroid` is the member's parcel as Leaflet [lat, lng]. Pass it whenever
 * you have it — `member.parcel_boundary` only covers the legacy `parcel_id`
 * link and is null for everyone linked through the parcel map, so relying on
 * it alone shipped shares with no location at all. See `resolveMemberCentroid`.
 */
export function buildMemberSharePayload(
  member: ShareableMember,
  centroid?: [number, number] | null,
): SharePayload {
  const address = [member.plot_number, member.street, member.area_suburb]
    .map((part) => (part || '').trim())
    .filter(Boolean)
    .join(', ');

  const point = centroid ?? memberBoundaryCentroid(member);

  const name = member.name || 'Member';

  return {
    title: name,
    kind: 'Property',
    headline: name,
    details: [
      ['Address', address],
      // Coordinates then directions, sitting with the address — all three
      // answer "where is this?". The raw pair matters on its own: it is what
      // gets read over a radio, and it survives being pasted anywhere, which
      // a Google Maps link does not. Same 5-decimal form as a dropped pin.
      ['Coordinates', point ? `${point[0].toFixed(5)}, ${point[1].toFixed(5)}` : null],
      ['Directions', point ? directionsUrl(point[0], point[1]) : null],
      // Then what's waiting on arrival, before the phone numbers: whoever is
      // heading out needs this before they get there. Empty lists render as
      // '' and buildShareText drops the row.
      ['Hazards', formatTags(member.hazard_tags)],
      ['Animals', formatTags(member.animal_tags)],
    ],
    location: point ? { lat: point[0], lng: point[1], label: name } : null,
    contacts: (member.contacts || []).map((c) => ({ name: c.name, phone: c.phone })),
  };
}
